// eslint-disable-next-line no-restricted-imports
import {
  Listbox as HeadlessListbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { type ReactNode, useId } from "react";

import { t } from "i18n/i18n";
import { SquareButton, type SquareButtonProps } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon } from "mds/icons";
import { ComponentEventPropertiesWithKey, trackEvent } from "utils/amplitude";

export type ListboxOption = {
  id: string;
  title: string;
  iconStart?: ReactNode;
};

export type ListboxDivider = { type: "divider" };

export type ListboxGroup<T extends ListboxOption = ListboxOption> = {
  type: "group";
  title: string;
  items: T[];
};

export type ListboxEntry<T extends ListboxOption = ListboxOption> =
  | T
  | ListboxDivider
  | ListboxGroup<T>;

export type ListboxProps<T extends ListboxOption> = ComponentEventPropertiesWithKey & {
  items: Array<ListboxEntry<T>>;
  selectedValue?: T["id"];
  className?: string;
  buttonKind?: SquareButtonProps["kind"];
  buttonSize?: SquareButtonProps["size"];
  buttonText?: ReactNode;
  onSelectionChange: (selectedItem: T) => void;
  disabled?: boolean;
  openMenuEventName?: string;
};

const isDivider = (entry: ListboxEntry<ListboxOption>): entry is ListboxDivider =>
  "type" in entry && entry.type === "divider";

const isGroup = <T extends ListboxOption>(entry: ListboxEntry<T>): entry is ListboxGroup<T> =>
  "type" in entry && entry.type === "group";

const findSelectedOption = <T extends ListboxOption>(
  items: Array<ListboxEntry<T>>,
  selectedValue: T["id"] | undefined,
): T | null => {
  if (selectedValue === undefined) {
    return null;
  }
  for (const entry of items) {
    if (isGroup(entry)) {
      const found = entry.items.find((item) => item.id === selectedValue);
      if (found) {
        return found;
      }
    } else if (!isDivider(entry) && entry.id === selectedValue) {
      return entry;
    }
  }
  return null;
};

const ListboxOptionRow = <T extends ListboxOption>({ item }: { item: T }) => (
  <ListboxOption
    className="flex cursor-pointer items-center gap-3 rounded-[8px] px-3 py-2 text-black-tint-20 data-[focus]:bg-black-tint-93 data-[selected]:bg-blue-shade-20 data-[selected]:data-[focus]:bg-blue data-[selected]:text-white supports-squircle:rounded-[16px] supports-squircle:corner-squircle"
    disabled={"disabled" in item ? (item.disabled as boolean) : false}
    value={item}
  >
    {item.iconStart}
    <span className="min-w-0 whitespace-normal break-words">{item.title}</span>
  </ListboxOption>
);

const ListboxGroupSection = <T extends ListboxOption>({ group }: { group: ListboxGroup<T> }) => {
  const headingId = useId();
  return (
    <div aria-labelledby={headingId} role="group">
      <div className="px-3 py-2 text-xs text-black-tint-55" id={headingId}>
        {group.title}
      </div>
      {group.items.map((item) => (
        <ListboxOptionRow item={item} key={item.id} />
      ))}
    </div>
  );
};

export const Listbox = <T extends ListboxOption>({
  items,
  selectedValue,
  className,
  buttonKind = "tertiary",
  buttonSize = "xs",
  buttonText,
  onSelectionChange,
  disabled,
  openMenuEventName,
  eventName,
  eventProperties,
  eventPropertyKey,
}: ListboxProps<T>) => {
  const selectedItem = findSelectedOption(items, selectedValue);

  const handleChange = (item: T) => {
    if (eventName) {
      trackEvent(eventName, {
        eventCategory: "Button press",
        ...eventProperties,
        [eventPropertyKey || "itemId"]: item.id,
      });
    }
    onSelectionChange(item);
  };

  return (
    <HeadlessListbox by="id" disabled={disabled} value={selectedItem} onChange={handleChange}>
      {({ open }) => (
        <>
          <ListboxButton
            as={SquareButton}
            className={className}
            disabled={disabled}
            eventName={openMenuEventName}
            isSelected={open}
            kind={buttonKind}
            size={buttonSize}
          >
            {!buttonText && selectedItem?.iconStart}
            <span className="min-w-0 truncate">
              {buttonText || selectedItem?.title || t("common.select_an_item")}
            </span>
            <ChevronDownIcon />
          </ListboxButton>
          <ListboxOptions
            anchor={{ to: "bottom start", padding: 12, gap: 4 }}
            // !fixed prevents additional scrollbar from appearing on the page when a listbox with a scrollbar is opened
            className="!fixed z-[110] min-w-[200px] !max-w-[560px] overflow-auto rounded-[12px] border border-black-tint-93 bg-white p-2 text-sm shadow-modal [--anchor-max-height:480px] supports-squircle:rounded-[24px] supports-squircle:corner-squircle"
            portal
          >
            {items.map((entry, index) => {
              if (isDivider(entry)) {
                return (
                  <hr
                    aria-hidden="true"
                    className="my-2 border-0 border-t border-black-tint-93"
                    // eslint-disable-next-line react/no-array-index-key
                    key={`divider-${index}`}
                  />
                );
              }
              if (isGroup(entry)) {
                return <ListboxGroupSection group={entry} key={`group-${entry.title}`} />;
              }
              return <ListboxOptionRow item={entry} key={entry.id} />;
            })}
          </ListboxOptions>
        </>
      )}
    </HeadlessListbox>
  );
};
